import React, { useRef, useState, useEffect } from "react";
import "./OfferCard.css";
import { roundPriceStep } from "../utils/roundPriceStep";

interface OfferCardProps {
  _id: string;
  trackId: string;
  searchQuery: string;
  trackArtists: string[];
  amount?: number | null;
  avatarUrl: string;
  status: "pending" | "accepted" | "queued";
  isPriority?: boolean;
  currency?: string;
  comment?: string;
  tier?: "standard" | "priority" | "fast" | null;
  createdAt?: string;
  clientNickname?: string | null;
  clientInstagram?: string | null;
  onAccept?: () => void;
  onDecline?: () => void;
  onPlay?: () => void;
}

const SWIPE_LIMIT = 180;

const truncate = (s: string, n: number) =>
  s.length > n ? s.slice(0, n) + "..." : s;

const round2 = (n: number) => Math.round(n * 100) / 100;

const formatTimestamp = (iso?: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (isToday) return time;
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${time}`;
};

const formatElapsed = (iso?: string) => {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
};

export default function OfferCard({
  trackId,
  searchQuery,
  trackArtists,
  amount,
  avatarUrl,
  status,
  isPriority = false,
  currency,
  comment,
  tier,
  createdAt,
  clientNickname,
  clientInstagram,
  onAccept,
  onDecline,
  onPlay,
}: OfferCardProps) {
  const [offset, setOffset] = useState(0);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const canSwipe = status === "pending" || status === "accepted" || status === "queued";
  const canSwipeOnlyDecline = status === "accepted" || status === "queued";

  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 480px)").matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  const startX = useRef<number | null>(null);
  const baseOff = useRef(0);
  const isPending = status === "pending";

  const normCur = (cur?: string) =>
    String(cur || "").trim().toLowerCase();

  const curSym = (cur?: string) => {
    switch (normCur(cur)) {
      case "usd": return "$";
      case "eur": return "€";
      case "gbp": return "£";
      case "rub": return "₽";
      default: return "$";
    }
  };

  const safeAmount = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  const normTier = (t: any) =>
    typeof t === "string" ? ((t as string).toLowerCase() as "standard" | "priority" | "fast") : t;

  const tierVal = normTier(tier);
  const isUpgrade = !!isPriority && (!tierVal || tierVal === "standard");

  const baseRounded = isUpgrade
    ? roundPriceStep(safeAmount * 2)
    : roundPriceStep(safeAmount);

  const totalRounded = baseRounded;
  const feeDisplay = round2(totalRounded - baseRounded);

  const getX = (e: React.TouchEvent | React.MouseEvent) =>
    "touches" in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;

  const handleStart = (e: React.TouchEvent | React.MouseEvent) => {
    if (!canSwipe) return;
    startX.current = getX(e);
    baseOff.current = offset;
  };

  const handleMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!canSwipe || startX.current == null) return;
    const delta = getX(e) - startX.current;
    const raw = baseOff.current + delta;
    const clamped = canSwipeOnlyDecline
      ? Math.max(-SWIPE_LIMIT, Math.min(0, raw))
      : Math.max(-SWIPE_LIMIT, Math.min(SWIPE_LIMIT, raw));
    setOffset(clamped);
  };

  const TRIGGER = 80;

  const triggerAccept = () => {
    setOffset(0);
    onAccept?.();
  };
  const triggerDecline = () => {
    setOffset(0);
    onDecline?.();
  };

  const handleEnd = () => {
    if (!canSwipe) return;
    let final = 0;
    if (!canSwipeOnlyDecline && offset >= TRIGGER) {
      final = SWIPE_LIMIT;
    } else if (offset <= -TRIGGER) {
      final = -SWIPE_LIMIT;
    }
    setOffset(final);
    startX.current = null;
  };

  const authorFull =
    trackArtists && trackArtists.length > 0
      ? trackArtists.join(", ")
      : "Unknown Artist";
  const titleFull = searchQuery || "";

  const authorDisplay = authorFull;
  const titleDisplay = titleFull;

  const hasPlayButton = status === "accepted" || status === "queued";
  const needsFade = status === "pending" || hasPlayButton;

  return (
    <>
      <div
        className={`offer-card ${isPending ? "is-pending" : ""}`}
        style={{
          borderColor: isPriority ? "gold" : undefined,
          boxShadow: isPriority ? "0 0 10px 3px rgba(255, 215, 0, 0.3)" : undefined,
        }}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
      >
        {isPending && (
          <div className="actions-layer actions-left" aria-hidden>
            <div className="action-pill accept" onClick={triggerAccept}>
              <span className="action-emoji" aria-hidden>✅</span>
              Accept
            </div>
          </div>
        )}

        {canSwipe && (
          <div className="actions-layer actions-right" aria-hidden>
            <div className="action-pill decline" onClick={triggerDecline}>
              <span className="action-emoji" aria-hidden>🛑</span>
              Decline
            </div>
          </div>
        )}

        <div
          className="card-inner"
          style={{ transform: `translateX(${offset}px)` }}
          onClick={() => {
            if (offset !== 0) setOffset(0);
          }}
        >
          {createdAt && (
            <span className="card-timestamp">
              <span className="card-timestamp-elapsed">{formatElapsed(createdAt)}</span>
              <span className="card-timestamp-dot">·</span>
              {formatTimestamp(createdAt)}
            </span>
          )}
          <div className="offer-left">
            {isPriority && (
              <div
                className="priority-wrapper"
                onClick={() => setShowPriorityModal(true)}
                title="Priority request — boosted"
              >
                <div className="boosted-amount">
                  {curSym(currency)}
                  {totalRounded.toFixed(2)}
                </div>
                <div className="priority-star">★</div>
              </div>
            )}

            <a
              href={`https://open.spotify.com/track/${trackId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={avatarUrl}
                alt="Song"
                className="song-avatar"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = "/fallback.png";
                }}
              />
            </a>

            <div className="song-details">
              <div className="song-author" title={authorFull}>
                {authorDisplay}
              </div>
              <div className="song-title" title={titleFull}>
                {titleDisplay}
              </div>

              <div
                className="song-amount"
                title={`Total: ${curSym(currency)}${totalRounded.toFixed(2)}`}
              >
                <span className="song-amount-value">
                  {curSym(currency)}{totalRounded.toFixed(2)}
                </span>
                {comment ? (
                  <span className="comment-text"> — "{comment}"</span>
                ) : (
                  ""
                )}
              </div>

              {(clientNickname || clientInstagram) && (
                <div className="song-client">
                  {clientNickname && <span className="client-nick">{clientNickname}</span>}
                  {clientInstagram && <span className="client-ig">@{clientInstagram}</span>}
                </div>
              )}
            </div>
          </div>

          {(status === "accepted" || status === "queued") && (
            <div className="offer-right">
              <button className="play-button" onClick={onPlay}>
                Play
              </button>
            </div>
          )}
        </div>
      </div>

      {showPriorityModal && (
        <div
          className="soon-modal-overlay"
          onClick={() => setShowPriorityModal(false)}
        >
          <div className="soon-modal" onClick={(e) => e.stopPropagation()}>
            <h3>⭐ Priority request</h3>
            <p>This song was boosted and will be prioritized in the queue.</p>
            <p style={{ opacity: 0.85, marginTop: 8 }}>
              Total charged: {curSym(currency)}
              {totalRounded.toFixed(2)}
              <br />
              Base: {curSym(currency)}
              {baseRounded.toFixed(2)} • Fees: {curSym(currency)}
              {feeDisplay.toFixed(2)}
            </p>
            <button onClick={() => setShowPriorityModal(false)}>OK</button>
          </div>
        </div>
      )}
    </>
  );
}
